import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { t } from 'i18next';
import { Fragment, ReactElement } from 'react';

import { Alert, Box, Card, Chip, Divider } from '@breathelife/mui';
import { Loader } from '@breathelife/ui-components';

import Typography from '../../../../../Components/Typography';
import { getLanguage } from '../../../../../Localization/utils';
import { Lead } from '../../../../../Models/Lead';
import { useFetchParticipantsUnderwritingRequirementsQuery } from '../../../../../ReactQuery/Participants/participants.queries';
import styled from '../../../../../Styles/themed-styled-components';

type UnderwritingRequirementsViewProps = {
  lead?: Lead;
};

dayjs.extend(relativeTime);

const DecisionChip = styled(Chip)`
  font-weight: 500;
  && {
    border-color: ${(props) => props.theme.colors.grey[40]};
    background-color: ${(props) => props.theme.colors.grey[20]};
    color: ${(props) => props.theme.colors.grey[60]};
  }
`;

const CounterChip = styled(Chip)`
  && {
    font-size: 15px;
    background-color: ${(props) => props.theme.colors.grey[70]};
    color: ${(props) => props.theme.colors.grey[10]};
    border-color: ${(props) => props.theme.colors.grey[70]};
  }
`;

export function UnderwritingRequirementsView({ lead }: UnderwritingRequirementsViewProps): ReactElement {
  const language = getLanguage();
  const applicationId = lead?.applications[0]?.id;

  const { data: requirementsPerParticipant = [], isFetching } = useFetchParticipantsUnderwritingRequirementsQuery(
    applicationId,
    {
      enabled: !!applicationId,
    },
  );

  const underwritingRequirementsItems = requirementsPerParticipant.map((participant) => {
    return (
      <Card variant='outlined' key={participant.id} sx={{ my: 2 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography gutterBottom variant='h2' component='div'>
            {`${participant?.data?.firstName || ''} ${participant?.data?.lastName || ''}`.trim()}
          </Typography>
          <CounterChip
            variant='outlined'
            label={participant?.underwritingRequirements?.[0].requirements.length ?? 0}
            size='small'
          />
        </Box>

        <Divider />
        <Box px={2} pt={2} pb={1}>
          {participant?.underwritingRequirements?.[0].requirements.map((requirement, index) => (
            <Box
              key={index}
              pb={1}
              display='flex'
              flexDirection='row'
              alignItems='baseline'
              justifyContent='space-between'
            >
              <Typography variant='body1' mr={2}>
                {requirement.text[language] ?? ''}
              </Typography>
              {requirement.decision && (
                <DecisionChip
                  key={requirement.id}
                  label={requirement.decisionText ? requirement.decisionText[language] : requirement.decision ?? ''}
                  size='small'
                  variant='outlined'
                  color='default'
                />
              )}
            </Box>
          ))}
        </Box>
      </Card>
    );
  });

  return (
    <Box
      py={2}
      px={2.5}
      sx={{
        overflow: 'hidden',
        overflowY: 'scroll',
      }}
    >
      {isFetching ? (
        <Loader />
      ) : underwritingRequirementsItems.length ? (
        <Fragment>
          <Alert severity='success'>{t('leadDetailDrawer.underwritingRequirementsView.success')}</Alert>
          {underwritingRequirementsItems}
        </Fragment>
      ) : (
        <Alert severity='warning'>{t('leadDetailDrawer.underwritingRequirementsView.warning')}</Alert>
      )}
    </Box>
  );
}
